import type { FlagsType } from '@iheartjane/feature-flags';
import { useEffect, useRef } from 'react';

import {
  registerMixpanelIdentity,
  registerSuperPropertiesStore,
  resetMixpanelIdentity,
} from '@jane/bloom/services/tracking/mixpanel';

type UseMixpanelRegistrationParams = {
  buildVersion: string;
  featureFlags?: FlagsType;
  operatorId: number;
  operatorName: string;
  pluginVersion?: string;
  projectToken?: string | null;
  storeCity?: string;
  storeId?: number;
  storeName?: string;
  storeState?: string;
  userId?: string;
};

/**
 * Registers Mixpanel identity and super properties. Matching boost/ecomm, only
 * signed-in users are identified; guests keep Mixpanel's persisted distinct id,
 * and we reset on logout.
 *
 * The pinned store can still change without an identity change — e.g.
 * URL-driven store overrides (`/store/:id/...`), custom slug routes, or
 * cookie/loader-data updates that bypass the StoreSwitcher — so we additionally
 * re-register the store-related super properties whenever `storeId` changes.
 * Without this, events fire with the previously-registered (often stale,
 * sometimes from a prior session via localStorage persistence) storeId super
 * prop.
 */
export const useMixpanelRegistration = ({
  projectToken,
  storeCity,
  storeId,
  storeName,
  storeState,
  userId,
  ...rest
}: UseMixpanelRegistrationParams) => {
  const hasRegisteredRef = useRef(false);
  const registeredUserIdRef = useRef<string | undefined>(undefined);
  const registeredStoreIdRef = useRef<number | null>(null);
  const signedInRef = useRef(false);

  useEffect(() => {
    if (!projectToken || !storeCity || !storeId || !storeName || !storeState) {
      return;
    }

    const identityChanged =
      !hasRegisteredRef.current || userId !== registeredUserIdRef.current;

    if (identityChanged) {
      // Reset on logout so a signed-in session doesn't carry into the next guest.
      if (!userId && signedInRef.current) {
        resetMixpanelIdentity();
      }

      hasRegisteredRef.current = true;
      signedInRef.current = !!userId;
      registeredUserIdRef.current = userId;
      registeredStoreIdRef.current = storeId;
      registerMixpanelIdentity({
        storeCity,
        storeId,
        storeName,
        storeState,
        userId,
        ...rest,
      });
    } else if (storeId !== registeredStoreIdRef.current) {
      registeredStoreIdRef.current = storeId;
      registerSuperPropertiesStore({
        storeCity,
        storeId,
        storeName,
        storeState,
      });
    }
  }, [projectToken, rest, storeCity, storeId, storeName, storeState, userId]);
};
